/**
 * All Reports View (Custom Report Engine — cross-tenant troubleshooting)
 *
 * super_admin-only platform view: lists EVERY custom report spec across all
 * tenants (and globals) in one table, so an operator can see what exists and
 * inspect the stored spec JSON to troubleshoot. Backed by GET
 * /report-engine/specs-all (super_admin, not consent-gated — metadata only).
 *
 * Each row can expand to show the raw `spec` JSONB verbatim (the key
 * troubleshooting artifact). Edit/Delete route into the existing authoring view
 * using each spec's OWN scope ('global' or its tenant UUID), reusing the same
 * ?scope= contract as ReportsView so deep-links stay consistent.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { reportEngineService } from '../../services/api';
import { formatDateTime } from '../../utils/formatters';

const ARCHETYPE_LABEL = { pivot: 'Pivot', listing: 'Listing', hierarchy: 'Hierarchy' };
const GLOBAL = 'global';

const AllReportsView = () => {
  const navigate = useNavigate();
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportEngineService.listAllSpecs();
      setSpecs(res.data || []);
    } catch (err) {
      console.error('Failed to load all specs', err);
      setError('Failed to load reports across tenants.');
      setSpecs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A spec's own scope param for edit/delete: global if tenant_id is null.
  const specScope = (s) => (s.tenant_id === null ? GLOBAL : s.tenant_id);

  const goEdit = (s) => {
    navigate('/admin/reports/' + s.id + '?scope=' + encodeURIComponent(specScope(s)));
  };

  const handleDelete = async (s) => {
    if (!window.confirm('Delete report "' + s.name + '"? This cannot be undone.')) return;
    try {
      const sc = specScope(s);
      await reportEngineService.deleteSpec(sc === GLOBAL ? null : sc, s.id);
      setNotice('Deleted "' + s.name + '".');
      load();
    } catch (err) {
      console.error('Delete failed', err);
      setError('Failed to delete the report.');
    }
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  // Client-side filter across tenant name + report name + archetype.
  const q = filter.trim().toLowerCase();
  const visible = q
    ? specs.filter((s) => {
        const hay = [
          s.name || '',
          s.archetype || '',
          s.tenant_name || (s.tenant_id === null ? 'global' : ''),
          s.tenant_slug || '',
        ].join(' ').toLowerCase();
        return hay.includes(q);
      })
    : specs;

  return (
    <div>
      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="alert alert-success mb-3">
          <Icons.CheckCircle size={18} />
          <span>{notice}</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="card mb-3" style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>
              Filter (tenant, report name, or type)
            </label>
            <input
              className="form-control"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter…"
            />
          </div>
          <button className="btn btn-secondary" onClick={load} disabled={loading}>
            <Icons.RefreshCw size={18} />
            Refresh
          </button>
        </div>
        {!loading && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
            {specs.length} report{specs.length === 1 ? '' : 's'} across all tenants
            {q ? ` — ${visible.length} matching "${filter.trim()}"` : ''}
          </div>
        )}
      </div>

      {loading ? (
        <div className="empty-state">
          <Icons.Loader size={48} className="spin" />
          <p>Loading reports…</p>
          <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : visible.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Icons.FileText size={64} />
            <h3>{specs.length === 0 ? 'No reports anywhere yet' : 'No matches'}</h3>
            <p>
              {specs.length === 0
                ? 'No custom reports exist for any tenant. Author one from Custom Reports.'
                : 'No reports match your filter.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Report</th>
                  <th>Tenant</th>
                  <th>Type</th>
                  <th>Updated</th>
                  <th style={{ width: '140px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => (
                  <React.Fragment key={s.id}>
                    <tr>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => toggleExpand(s.id)}
                          title={expandedId === s.id ? 'Hide JSON' : 'View JSON'}
                        >
                          <Icons.Eye size={14} />
                        </button>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{s.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>id #{s.id}</div>
                      </td>
                      <td>
                        {s.tenant_id === null
                          ? <span className="badge badge-success">🌐 Global</span>
                          : <span>{s.tenant_name || <span style={{ color: 'var(--text-muted)' }}>{s.tenant_id}</span>}</span>}
                      </td>
                      <td>
                        <span className="badge badge-neutral">{ARCHETYPE_LABEL[s.archetype] || s.archetype}</span>
                      </td>
                      <td>{formatDateTime(s.updated_at || s.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => goEdit(s)} title="Edit">
                            <Icons.Edit size={14} />
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleDelete(s)} title="Delete">
                            <Icons.Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === s.id && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--bg-subtle, rgba(0,0,0,0.15))' }}>
                          <div style={{ padding: '8px 4px' }}>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                              Stored spec JSON (archetype: {s.archetype})
                            </div>
                            <pre
                              style={{
                                margin: 0,
                                padding: '12px',
                                borderRadius: '6px',
                                background: 'var(--bg-code, #0b1020)',
                                color: 'var(--text-code, #d6e0ff)',
                                fontSize: '12px',
                                lineHeight: 1.5,
                                overflowX: 'auto',
                                whiteSpace: 'pre',
                              }}
                            >
                              {JSON.stringify(s.spec, null, 2)}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AllReportsView;
